import React, { useState, useEffect } from "react";
import Select from "react-select";
import axios from "axios";
import { useAuth } from "../../../context/auth-context";
import { formatToZMW, rootUrl } from "../../../helpers";
import DataTable from "react-data-table-component";
import { FaPlus, FaTimes, FaCoins, FaEye, FaMoneyBillWave } from "react-icons/fa";
import { useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import { customStyles } from "../../../styles";
import LoanSummary from "./LoanSummary";

export default function Loans() {
  const { allData } = useOutletContext();
  const auth = useAuth();
  const [loans, setLoans] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDisburseModal, setShowDisburseModal] = useState(false);
  const [showSummaryPanel, setShowSummaryPanel] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [summarySid, setSummarySid] = useState(null);
  const [loading, setLoading] = useState(true);

  const [paymentForm, setPaymentForm] = useState({
    customer: null,
    amount: "",
    description: "Loan repayment",
    bank: null,
    sof: null,
    colce: null,
    invoice: "",
    date: new Date().toISOString().split("T")[0],
  });

  const [disburseForm, setDisburseForm] = useState({
    customer: null,
    amount: "",
    description: "Loan disbursement",
    bank: null,
    sof: null,
    project: null,
    invoice: "",
    date: new Date().toISOString().split("T")[0],
  });

  const fetchLoans = async () => {
    try {
      setLoading(true);
      const response = await axios.get(rootUrl(`/getLoans/${auth.user.id}`));
      setLoans(response.data.data || []);
    } catch (error) {
      console.error("Error fetching loans:", error);
      toast.error("Failed to fetch loans");
      setLoans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLoans();
  }, []);

  const openAddPayment = (loan) => {
    setSelectedLoan(loan);
    setPaymentForm({
      customer: { value: loan.sid, label: loan.name },
      amount: "",
      description: "Loan repayment",
      bank: null,
      sof: null,
      colce: allData?.cc?.[0] ? { value: allData.cc[0].id, label: allData.cc[0].collection_name } : null,
      invoice: "",
      date: new Date().toISOString().split("T")[0],
    });
    setShowPaymentModal(true);
    setShowSummaryPanel(false);
  };

  const openDisburseModal = () => {
    setDisburseForm({
      customer: null,
      amount: "",
      description: "Loan disbursement",
      bank: null,
      sof: null,
      project: null,
      invoice: "",
      date: new Date().toISOString().split("T")[0],
    });
    setShowDisburseModal(true);
  };

  const openLoanSummary = (loan) => {
    setSummarySid(loan.sid);
    setSelectedLoan(loan);
    setShowSummaryPanel(true);
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedLoan(null);
  };

  const closeDisburseModal = () => setShowDisburseModal(false);
  const closeSummaryPanel = () => {
    setShowSummaryPanel(false);
    setSummarySid(null);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (!paymentForm.bank?.value) {
      toast.error("Please select a bank");
      return;
    }
    if (!paymentForm.sof?.value) {
      toast.error("Please select source of funds");
      return;
    }
    if (!paymentForm.colce?.value) {
      toast.error("Please select collection center");
      return;
    }

    const payload = {
      uid: auth.user.id,
      customer: paymentForm.customer,
      amount: paymentForm.amount,
      description: paymentForm.description,
      bank: paymentForm.bank,
      sof: paymentForm.sof,
      colce: paymentForm.colce,
      invoice: paymentForm.invoice,
      date: paymentForm.date,
    };

    try {
      const response = await axios.post(rootUrl("/addLoanPayment"), payload);
      if (response.data.status === 200) {
        toast.success("Loan repayment recorded successfully");
        closePaymentModal();
        fetchLoans();
      } else {
        toast.error(response.data.msg || "Something went wrong");
      }
    } catch (error) {
      toast.error(error?.response?.data?.msg || error?.message || "Failed to add payment");
    }
  };

  const handleDisburseSubmit = async (e) => {
    e.preventDefault();
    if (!disburseForm.customer?.value) {
      toast.error("Please select a borrower");
      return;
    }
    if (!disburseForm.amount || parseFloat(disburseForm.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (!disburseForm.bank?.value) {
      toast.error("Please select a bank");
      return;
    }
    if (!disburseForm.sof?.value) {
      toast.error("Please select source of funds");
      return;
    }

    const payload = {
      uid: auth.user.id,
      customer: disburseForm.customer,
      amount: disburseForm.amount,
      description: disburseForm.description,
      bank: disburseForm.bank,
      sof: disburseForm.sof,
      project: disburseForm.project || { value: 0 },
      invoice: disburseForm.invoice,
      date: disburseForm.date,
    };

    try {
      const response = await axios.post(rootUrl("/addLoanDisbursement"), payload);
      if (response.data.status === 200) {
        toast.success("Loan disbursed successfully");
        closeDisburseModal();
        fetchLoans();
      } else {
        toast.error(response.data.msg || "Something went wrong");
      }
    } catch (error) {
      toast.error(error?.response?.data?.msg || error?.message || "Failed to disburse loan");
    }
  };

  const handleAddPaymentFromSummary = (loan) => {
    closeSummaryPanel();
    openAddPayment(loan);
  };

  const borrowerOptions = (allData?.suppliers || []).map((s) => ({
    value: s.id,
    label: `${s.name}${s.person ? ` (${s.person})` : ""}`,
  }));

  const columns = [
    {
      name: "Sn",
      selector: (row, index) => index + 1,
      sortable: true,
      width: "50px",
    },
    {
      name: "Borrower",
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <div>
          <div className="font-medium text-gray-900">{row.name}</div>
          {row.person && (
            <div className="text-xs text-gray-500">{row.person}</div>
          )}
        </div>
      ),
    },
    {
      name: "Principal",
      selector: (row) => row.principal,
      sortable: true,
      cell: (row) => formatToZMW(row.principal),
    },
    {
      name: "Interest (5%)",
      selector: (row) => row.interest,
      sortable: true,
      cell: (row) => formatToZMW(row.interest),
    },
    {
      name: "Total Owed",
      selector: (row) => row.totalOwed,
      sortable: true,
      cell: (row) => formatToZMW(row.totalOwed),
    },
    {
      name: "Payments",
      selector: (row) => row.payments,
      sortable: true,
      cell: (row) => formatToZMW(row.payments),
    },
    {
      name: "Balance",
      selector: (row) => row.balance,
      sortable: true,
      cell: (row) => (
        <span className="font-semibold text-blue-950">
          {formatToZMW(row.balance)}
        </span>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="flex items-center gap-2">
          <button
            className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1 rounded-lg text-xs font-medium transition-colors"
            onClick={() => openLoanSummary(row)}
            title="View loan summary"
          >
            <FaEye className="text-[10px]" />
            View
          </button>
          <button
            className="inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white px-2.5 py-1 rounded-lg text-xs font-medium transition-colors"
            onClick={() => openAddPayment(row)}
            disabled={row.balance <= 0}
            title={row.balance <= 0 ? "Loan fully paid" : "Add repayment"}
          >
            <FaPlus className="text-[10px]" />
            Repay
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-950 to-blue-800 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 rounded-xl p-3">
              <FaCoins className="text-2xl" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Loans</h1>
              <p className="text-blue-100 text-sm mt-1">
                Disburse loans and record repayments. Single-entry disbursements (COA 321040), 5% interest.
              </p>
            </div>
          </div>
          <button
            onClick={openDisburseModal}
            className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white h-10 px-4 rounded-lg text-sm font-medium transition-colors border border-white/30"
          >
            <FaMoneyBillWave className="text-lg" />
            Disburse Loan
          </button>
        </div>
      </div>

      {/* Loans Table */}
      <div className="bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-gray-100">
        <p className="text-xs text-gray-500 mb-4">
          All loans disbursed from this page. Repayments reduce the balance.
        </p>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-950"></div>
          </div>
        ) : loans.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FaCoins className="mx-auto text-4xl text-gray-300 mb-3" />
            <p>No loans yet.</p>
            <p className="text-sm mt-1">
              Click &quot;Disburse Loan&quot; to add a new loan.
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-gray-100 overflow-hidden">
            <DataTable
              columns={columns}
              data={loans}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={customStyles}
            />
          </div>
        )}
      </div>

      {/* Loan Summary Sliding Panel */}
      {showSummaryPanel && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div
            className="flex-1 bg-black/40"
            onClick={closeSummaryPanel}
          />
          <div className="bg-white h-full w-full sm:w-4/5 lg:w-2/3 xl:w-1/2 shadow-2xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between bg-gradient-to-r from-blue-950 to-blue-800 px-6 py-4 text-white border-b border-gray-200">
              <h3 className="font-semibold text-lg">Loan Summary</h3>
              <button
                onClick={closeSummaryPanel}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <FaTimes />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6">
              <LoanSummary
                sid={summarySid}
                onAddPayment={handleAddPaymentFromSummary}
                onClose={closeSummaryPanel}
              />
            </div>
          </div>
        </div>
      )}

      {/* Add Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 border-b pb-4">
              <h2 className="text-xl font-semibold text-gray-800">Add Loan Repayment</h2>
              <button onClick={closePaymentModal} className="p-2 hover:bg-gray-100 rounded-lg">
                <FaTimes />
              </button>
            </div>
            {selectedLoan && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Borrower:</span> {selectedLoan.name}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Outstanding Balance:</span>{" "}
                  {formatToZMW(selectedLoan.balance)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Receipt with budget line COA 157004
                </p>
              </div>
            )}
            <form onSubmit={handlePaymentSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  placeholder="Enter payment amount"
                  className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  placeholder="Loan repayment"
                  className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={paymentForm.description}
                  onChange={(e) => setPaymentForm({ ...paymentForm, description: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bank</label>
                <Select
                  options={allData?.bank?.map((b) => ({ value: b.id, label: b.name }))}
                  value={paymentForm.bank}
                  onChange={(selected) => setPaymentForm({ ...paymentForm, bank: selected })}
                  placeholder="Select bank"
                  isClearable
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source of Funds</label>
                <Select
                  options={allData?.sof?.map((s) => ({ value: s.id, label: s.source_of_fund }))}
                  value={paymentForm.sof}
                  onChange={(selected) => setPaymentForm({ ...paymentForm, sof: selected })}
                  placeholder="Select source of funds"
                  isClearable
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Collection Center</label>
                <Select
                  options={allData?.cc?.map((c) => ({ value: c.id, label: c.collection_name }))}
                  value={paymentForm.colce}
                  onChange={(selected) => setPaymentForm({ ...paymentForm, colce: selected })}
                  placeholder="Select collection center"
                  isClearable
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cheque # / Reference</label>
                <input
                  type="text"
                  placeholder="Cheque or reference number"
                  className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={paymentForm.invoice}
                  onChange={(e) => setPaymentForm({ ...paymentForm, invoice: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  required
                  className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={paymentForm.date}
                  onChange={(e) => setPaymentForm({ ...paymentForm, date: e.target.value })}
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closePaymentModal}
                  className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2.5 rounded-lg font-medium"
                >
                  Add Repayment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Disburse Loan Modal */}
      {showDisburseModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 border-b pb-4">
              <h2 className="text-xl font-semibold text-gray-800">Disburse Loan</h2>
              <button onClick={closeDisburseModal} className="p-2 hover:bg-gray-100 rounded-lg">
                <FaTimes />
              </button>
            </div>
            <div className="mb-4 p-3 bg-amber-50 rounded-lg">
              <p className="text-sm text-amber-800">
                Single entry: records loan (COA 321040) + cashbook expense. No invoice/payment pair.
              </p>
            </div>
            <form onSubmit={handleDisburseSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Borrower</label>
                <Select
                  options={borrowerOptions}
                  value={disburseForm.customer}
                  onChange={(selected) => setDisburseForm({ ...disburseForm, customer: selected })}
                  placeholder="Select borrower (supplier)"
                  isClearable
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Add new payees from Payments page first if needed.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  placeholder="Loan amount"
                  className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={disburseForm.amount}
                  onChange={(e) => setDisburseForm({ ...disburseForm, amount: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  placeholder="Loan disbursement"
                  className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={disburseForm.description}
                  onChange={(e) => setDisburseForm({ ...disburseForm, description: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bank</label>
                <Select
                  options={allData?.bank?.map((b) => ({ value: b.id, label: b.name }))}
                  value={disburseForm.bank}
                  onChange={(selected) => setDisburseForm({ ...disburseForm, bank: selected })}
                  placeholder="Select bank"
                  isClearable
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source of Funds</label>
                <Select
                  options={allData?.sof?.map((s) => ({ value: s.id, label: s.source_of_fund }))}
                  value={disburseForm.sof}
                  onChange={(selected) => setDisburseForm({ ...disburseForm, sof: selected })}
                  placeholder="Select source of funds"
                  isClearable
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project (optional)</label>
                <Select
                  options={allData?.projects?.map((p) => ({ value: p.id, label: p.description || p.code })) || []}
                  value={disburseForm.project}
                  onChange={(selected) => setDisburseForm({ ...disburseForm, project: selected })}
                  placeholder="Select project"
                  isClearable
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PV / Cheque #</label>
                <input
                  type="text"
                  placeholder="Payment voucher or cheque number"
                  className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={disburseForm.invoice}
                  onChange={(e) => setDisburseForm({ ...disburseForm, invoice: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  required
                  className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={disburseForm.date}
                  onChange={(e) => setDisburseForm({ ...disburseForm, date: e.target.value })}
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeDisburseModal}
                  className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-lg font-medium"
                >
                  Disburse Loan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
